/**
 * Fair Price Coach overlay
 *
 * Live plain-English commentary on what the JJ Simon strategy is doing right
 * now: where fair price is, which phase we're in, whether the setup is armed,
 * what to do about it, and what trigger to wait for.
 *
 * The chart draws lines; this panel says what they mean. All the reasoning
 * lives in utils/fairPriceCommentary.ts (pure + unit-tested) — this component
 * only renders it and optionally speaks it.
 *
 * Speech uses the browser's built-in SpeechSynthesis (no network, no deps) and
 * is OFF by default. It only speaks when the verdict actually changes, so it
 * won't chatter on every tick.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { Volume2, VolumeX } from 'lucide-react';
import { useFairPriceStore } from '../../store/fairPriceStore';
import { useDisplacementStore } from '../../store/displacementStore';
import { buildFairPriceCommentary, Tone } from '../../utils/fairPriceCommentary';
import './FairPriceCoachOverlay.css';

interface FairPriceCoachOverlayProps {
  /** Chart symbol, used to filter signals/markers to this chart. */
  symbol: string;
  /** Current price, null when unknown. */
  price: number | null;
  /** Normalises a contract symbol to its base (e.g. MNQU6.CME -> MNQ). */
  normalizeToBase: (symbol: string) => string;
}

const TONE_CLASS: Record<Tone, string> = {
  go: 'fpc-tone-go',
  wait: 'fpc-tone-wait',
  stop: 'fpc-tone-stop',
  muted: 'fpc-tone-muted',
};

const SPEECH_STORAGE_KEY = 'fairPriceCoach.speak';

function loadSpeak(): boolean {
  try {
    return localStorage.getItem(SPEECH_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function persistSpeak(value: boolean): void {
  try {
    localStorage.setItem(SPEECH_STORAGE_KEY, String(value));
  } catch {
    // storage blocked — non-fatal
  }
}

/** Re-render on a timer so the countdowns ("42m left") stay honest. */
function useTick(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const FairPriceCoachOverlay: React.FC<FairPriceCoachOverlayProps> = ({
  symbol,
  price,
  normalizeToBase,
}) => {
  const now = useTick(15_000);
  const [speak, setSpeak] = useState<boolean>(loadSpeak);

  // useShallow: these selectors build new objects/arrays (repo zustand rule).
  const base = normalizeToBase(symbol);

  // State MUST be read per-symbol: the engine streams updates for every watched
  // symbol, so the shared `state` field holds whichever arrived last and would
  // put ES numbers on an MNQ chart.
  const { enabled, state, settings, signals } = useFairPriceStore(
    useShallow((s) => ({
      enabled: s.enabled,
      state: s.statesBySymbol[base] ?? null,
      settings: s.settings,
      signals: s.signals,
    }))
  );
  const displacements = useDisplacementStore(useShallow((s) => s.markers));

  const commentary = useMemo(() => {
    const mySignals = signals.filter((s) => normalizeToBase(s.symbol) === base);
    const myDisplacements = displacements.filter((d) => normalizeToBase(d.symbol) === base);
    return buildFairPriceCommentary({
      enabled,
      state,
      settings,
      signals: mySignals,
      displacements: myDisplacements,
      price,
      now,
    });
  }, [enabled, state, settings, signals, displacements, price, now, base, normalizeToBase]);

  // --- Speech: only on a genuine verdict change, never on every tick -------
  const lastSpokenRef = useRef<string>('');
  useEffect(() => {
    if (!speak) return;
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) return;

    const key = `${commentary.headline.verdict}|${commentary.headline.detail}`;
    if (key === lastSpokenRef.current) return;
    lastSpokenRef.current = key;

    try {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(commentary.speech);
      utterance.rate = 1.05;
      utterance.pitch = 1;
      window.speechSynthesis.speak(utterance);
    } catch {
      // Speech is a nicety — never let it break the panel.
    }
  }, [speak, commentary]);

  // Stop mid-sentence when muted or unmounted.
  useEffect(() => {
    if (speak) return;
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
  }, [speak]);

  useEffect(
    () => () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    },
    []
  );

  const toggleSpeak = (): void => {
    const next = !speak;
    persistSpeak(next);
    setSpeak(next);
  };

  const { headline } = commentary;

  return (
    <div className="fpc-panel">
      <div className="fpc-header">
        <div className={`fpc-verdict ${TONE_CLASS[headline.tone]}`}>
          <span className="fpc-verdict-dot" />
          {headline.verdict}
        </div>
        <button
          className={`fpc-speak-btn ${speak ? 'fpc-speak-on' : ''}`}
          onClick={toggleSpeak}
          title={speak ? 'Mute spoken commentary' : 'Read the commentary aloud when it changes'}
        >
          {speak ? <Volume2 size={11} /> : <VolumeX size={11} />}
        </button>
      </div>

      <div className="fpc-detail">{headline.detail}</div>

      {commentary.situation.length > 0 && (
        <div className="fpc-section">
          <div className="fpc-section-title">Where we are</div>
          {commentary.situation.map((line, i) => (
            <div key={i} className="fpc-line">
              {line}
            </div>
          ))}
        </div>
      )}

      {commentary.lookFor.length > 0 && (
        <div className="fpc-section">
          <div className="fpc-section-title">What to look for</div>
          {commentary.lookFor.map((line, i) => (
            <div key={i} className="fpc-line fpc-bullet">
              {line}
            </div>
          ))}
        </div>
      )}

      {commentary.plan && (
        <div className="fpc-section">
          <div className="fpc-section-title">If it triggers</div>
          <div className="fpc-line fpc-plan">{commentary.plan}</div>
        </div>
      )}

      {commentary.live.length > 0 && (
        <div className="fpc-section">
          <div className="fpc-section-title">In play</div>
          {commentary.live.map((line, i) => (
            <div key={i} className="fpc-line fpc-live">
              {line}
            </div>
          ))}
        </div>
      )}

      {commentary.warnings.length > 0 && (
        <div className="fpc-section">
          <div className="fpc-section-title">Heads up</div>
          {commentary.warnings.map((line, i) => (
            <div key={i} className="fpc-line fpc-warn">
              {line}
            </div>
          ))}
        </div>
      )}

      {commentary.score && <div className="fpc-score">{commentary.score}</div>}
    </div>
  );
};

export default FairPriceCoachOverlay;
